import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import './App.css'

// 導入組件
import Header from './components/Header'
import Login from './components/Login'
import Sidebar from './components/Sidebar'

// 導入安全工具
import { initCSRFProtection } from './lib/csrf'
import logger from './lib/logger'

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // 初始化安全機制
  useEffect(() => {
    // 初始化 CSRF 保護
    initCSRFProtection()
    logger.info('應用程式已啟動，CSRF 保護已初始化')
  }, [])

  // 檢查用戶登入狀態
  useEffect(() => {
    checkAuthStatus()

    // 設定定期檢查權限更新（每30秒檢查一次）
    const permissionCheckInterval = setInterval(() => {
      if (currentUser) {
        checkAuthStatus(true) // 靜默權限檢查
      }
    }, 30000) // 30秒

    // 監聽頁面可見性變化，當用戶回到頁面時檢查權限
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser) {
        checkAuthStatus(true) // 當頁面變為可見時立即檢查權限
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // 清理定時器和事件監聽器
    return () => {
      clearInterval(permissionCheckInterval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const checkAuthStatus = async (silentUpdate = false) => {
    try {
      const response = await fetch('/api/user/auth/status', {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authenticated && data.user) {
          setCurrentUser(data.user)
        } else {
          setCurrentUser(null)
        }
      } else {
        console.error('檢查登入狀態失敗:', response.status, response.statusText)
        setCurrentUser(null)
      }
    } catch (error) {
      console.error('檢查登入狀態網路錯誤:', error)
      setCurrentUser(null)
    } finally {
      if (!silentUpdate) {
        setLoading(false)
      }
    }
  }

  const handleLogin = (userData) => {
    setCurrentUser(userData)
  }

  const handleLogout = async () => {
    try {
      await fetch('/api/user/logout', {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      console.error('登出失敗:', error)
    } finally {
      setCurrentUser(null)
    }
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  // 載入中狀態
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">載入中...</p>
        </div>
      </div>
    )
  }

  // 未登入時顯示登入頁面
  if (!currentUser) {
    return <Login onLogin={handleLogin} />
  }

  // 已登入時顯示主應用
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* 頂部導航 */}
      <Header
        user={currentUser}
        onToggleSidebar={toggleSidebar}
        onLogout={handleLogout}
        sidebarOpen={sidebarOpen}
      />

      <div className="app-layout-container">
        {/* 側邊欄 */}
        <motion.div
          initial={false}
          animate={{
            width: sidebarOpen ? '280px' : '80px',
            transition: { duration: 0.3, ease: 'easeInOut' }
          }}
          className="sidebar-container bg-white dark:bg-gray-800 shadow-lg transition-colors duration-200"
        >
          <Sidebar
            isOpen={sidebarOpen}
            currentUser={currentUser}
          />
        </motion.div>

        {/* 主要內容區域 */}
        <motion.main
          className="main-content-container"
          initial={false}
          animate={{
            marginLeft: sidebarOpen ? '280px' : '80px',
            transition: { duration: 0.3, ease: 'easeInOut' }
          }}
        >
          <div className="p-6">
            <Outlet context={{ currentUser }} />
          </div>
        </motion.main>
      </div>
    </div>
  )
}

export default App